/**
 * Building Query Hooks - All Building API Calls with Caching
 * 
 * CACHED:
 * - Dashboard Buildings
 * - Hidden Buildings  
 * - Aggregated GWP Data (Cards)
 * - LCA Stage Data
 * - Building Results
 * - Process Data
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building } from '../db_access/building';
import { Process } from '../db_access/process';
import { CacheDebugger } from './queryClient';

// -------------------------------------------------------------
// DASHBOARD HOOKS
// -------------------------------------------------------------

/**
 * Fetch all buildings for dashboard (cached 2 min)
 */
export const useBuildingsDashboard = () => {
    return useQuery({
        queryKey: ['buildings', 'dashboard'],
        queryFn: async () => {
            const res = await Building.get_non_archive_by_user_id();
            const data = await res.json();

            const buildings = data.map(b => ({
                ...b,
                year: b.Year ?? b.year ?? null,
                is_published_template: b.is_published_template === 1 || b.is_published_template === true,
                gwp: parseFloat(b.gwp || 0),
                biogenic_gwp: parseFloat(b.biogenic_gwp || 0)
            }));

            CacheDebugger.fetch('Dashboard Buildings', buildings.length);
            return buildings;
        },
        staleTime: 1000 * 60 * 2, // 2 min fresh
    });
};

/**
 * Fetch hidden buildings (cached 5 min)
 */
export const useHiddenBuildings = () => {
    return useQuery({
        queryKey: ['buildings', 'hidden'],
        queryFn: async () => {
            const res = await Building.get_hidden();
            const data = await res.json();
            CacheDebugger.fetch('Hidden Buildings', data.length);
            return data;
        },
        staleTime: 1000 * 60 * 5,
    });
};

/**
 * Fetch aggregated GWP data for cards (cached 5 min)
 */
export const useBuildingAggregatedData = (buildingIds = []) => {
    // Sort to keep the query key stable even if selection order changes
    const sortedIdsString = [...buildingIds].sort().join(',');
    const queryKey = ['buildings', 'aggregated', sortedIdsString || 'none'];

    return useQuery({
        queryKey,
        queryFn: async () => {
            if (buildingIds.length === 0) {
                return {
                    totalGwp: 0,
                    stages: { "A1-A3": 0, "A4-A5": 0, "B1-B5": 0, "B6-B7": 0, "C": 0, "D": 0 },
                    types: { Material: 0, Transport: 0, Energy: 0 }
                };
            }

            const res = await Building.get_global_view_data(buildingIds);
            const data = await res.json();

            CacheDebugger.fetch('Aggregated GWP Data', buildingIds.length + ' buildings');

            return {
                totalGwp: data.totalGwp || 0,
                stages: data.stages || {},
                types: data.types || {}
            };
        },
        enabled: buildingIds.length > 0,
        staleTime: 1000 * 60 * 5,
    });
};

// -------------------------------------------------------------
// WORKSPACE HOOKS
// -------------------------------------------------------------

/**
 * Fetch building by ID (cached 5 min)
 */
export const useBuildingById = (buildingId) => {
    return useQuery({
        queryKey: ['building', buildingId],
        queryFn: async () => {
            const res = await Building.get_by_id(buildingId);
            const data = await res.json();
            CacheDebugger.fetch(`Building ${buildingId}`);
            return data; // Returns single object directly from backend
        },
        enabled: !!buildingId,
        staleTime: 1000 * 60 * 5,
    });
};

// -------------------------------------------------------------
// RESULT TAB HOOKS
// -------------------------------------------------------------

/**
 * Fetch building LCA results (cached 5 min)
 */
export const useBuildingResults = (buildingId, filter) => {
    return useQuery({
        queryKey: ['building', buildingId, 'results', filter?.category, filter?.subcategory],
        queryFn: async () => {
            const payload = {
                building_id: buildingId,
                filter_category: filter?.category,
                filter_subcategory: filter?.subcategory
            };
            const res = await Building.get_results(payload);
            const data = await res.json();

            CacheDebugger.fetch('Building Results');
            return data;
        },
        enabled: !!buildingId,
        staleTime: 1000 * 60 * 5,
    });
};

/**
 * Fetch process data for system boundary (cached 10 min)
 */
export const useBuildingProcessData = (buildingId) => {
    return useQuery({
        queryKey: ['building', buildingId, 'process'],
        queryFn: async () => {
            const res = await Process.get_by_building_ids([buildingId]);
            const data = await res.json();

            CacheDebugger.fetch('Process Data');
            return data;
        },
        enabled: !!buildingId,
        staleTime: 1000 * 60 * 10,
    });
};

/**
 * Fetch AI insight (cached 30 min)
 */
export const useBuildingInsight = (buildingId) => {
    return useQuery({
        queryKey: ['building', buildingId, 'insight'],
        queryFn: async () => {
            const res = await Building.get_insight(buildingId);
            const data = await res.json();

            CacheDebugger.fetch('AI Insight');
            return data;
        },
        enabled: !!buildingId,
        staleTime: 1000 * 60 * 30,
    });
};

// -------------------------------------------------------------
// CACHE INVALIDATION HELPER
// -------------------------------------------------------------

export const useInvalidateBuildingCache = () => {
    const queryClient = useQueryClient();

    return {
        // Invalidate specific building
        invalidateBuilding: (buildingId) => {
            queryClient.invalidateQueries({ queryKey: ['building', buildingId] });
        },

        // Invalidate dashboard data
        invalidateDashboard: () => {
            queryClient.invalidateQueries({ queryKey: ['buildings'] });
        },

        // Invalidate everything
        invalidateAll: () => {
            queryClient.invalidateQueries({ queryKey: ['building'] });
            queryClient.invalidateQueries({ queryKey: ['buildings'] });
        }
    };
};
